import React, { useState } from 'react';
import classnames from '../../utils/classnames.tsx';
import DropdownMenu from '../DropdownMenu.tsx';
import Button from '../Button.tsx';
import { useTranslation } from 'react-i18next';
import { BOOLEAN_CONSTANT_SUGGESTION, DAY_CONSTANT_SUGGESTION, NUMBER_CONSTANT_SUGGESTION } from './constants.ts';

export interface Token {
    type: 'variable' | 'constant' | 'operator' | 'function' | 'group';
    value: string;
    tokens?: (Token | null)[];
    slotCount?: number;
}

export interface AllowedTokens {
    allowedVariables?: string[];
    allowedFunctions?: { name: string; slotCount: number }[];
    allowedOperators?: string[];
    allowGroups?: boolean;
}

const BaseToken = ({ children, className, ...props }: React.ComponentPropsWithRef<'span'>) => {
    return (
        <span
            className={classnames(
                'px-1.5 h-6 text-gray-500 font-medium flex items-center justify-center text-sm leading-[1.8] rounded transition-colors select-none whitespace-nowrap',
                props.onClick && 'cursor-pointer',
                className
            )}
            {...props}
        >
            {children}
        </span>
    );
};

const EmptyToken = ({ className, ...props }: React.ComponentPropsWithRef<typeof BaseToken>) => {
    return (
        <BaseToken
            className={classnames(
                'w-6 bg-gray-200/50 border border-gray-400',
                props.onClick && 'hover:bg-gray-300/50',
                className
            )}
            {...props}
        />
    );
};

const VariableToken = ({ className, children, ...props }: React.ComponentPropsWithRef<typeof BaseToken>) => {
    return (
        <BaseToken
            className={classnames('text-blue-600 bg-blue-100', props.onClick && 'hover:bg-blue-200', className)}
            {...props}
        >
            {children}
        </BaseToken>
    );
};

const OperatorToken = ({ className, children, ...props }: React.ComponentPropsWithRef<typeof BaseToken>) => {
    return (
        <BaseToken
            className={classnames(
                'text-amber-600 bg-amber-100 min-w-6',
                props.onClick && 'hover:bg-amber-200 ',
                className
            )}
            {...props}
        >
            {children}
        </BaseToken>
    );
};

const FunctionToken = ({
    className,
    children,
    slotCount,
    onClick,
    tokens = [],
    onTokensChange,
    allowedVariables = [],
    allowedFunctions = [],
    allowedOperators = [],
    allowGroups = false,
    ...props
}: React.ComponentPropsWithRef<typeof BaseToken> &
    AllowedTokens & {
        slotCount?: number;
        tokens?: (Token | null)[];
        onTokensChange?: (tokens: (Token | null)[]) => void;
    }) => {
    // slot is either null (empty) or filled with a token. number of items is fixed by slotsCount
    const slots = slotCount && slotCount > 0 ? [...Array(slotCount).keys()].map((i) => tokens?.[i] || null) : [];

    const handleAddToken = (token: Token, index: number) => {
        const newTokens = [...tokens];
        newTokens.splice(index + 1, 0, token);
        onTokensChange(newTokens);
    };

    const handleRemoveToken = (index: number) => {
        const newTokens = [...tokens];
        newTokens.splice(index, 1);
        onTokensChange(newTokens);
    };

    const handleClick = (e) => {
        if (!onTokensChange || (e.currentTarget === e.target && onClick)) {
            onClick(e);
        } else {
            e.stopPropagation();
        }
    };

    return (
        <BaseToken
            className={classnames('text-purple-600 bg-purple-100', onClick && 'hover:bg-purple-200', className)}
            onClick={handleClick}
            {...props}
        >
            {children}(
            {slots.length > 0 && (
                <span className="inline-flex gap-1 px-1">
                    {slots.map((slot, index) => {
                        const { type: tokenType, value, ...tokenProps } = slot || {};
                        const Component = TOKEN_TYPE_COMPONENTS[tokenType];

                        return (
                            <React.Fragment key={index}>
                                {slot ? (
                                    <Component {...tokenProps} onClick={() => handleRemoveToken(index)}>
                                        {value}
                                    </Component>
                                ) : onTokensChange ? (
                                    <AddTokenDropdownMenu
                                        onTokenSelect={(token) => handleAddToken(token, index)}
                                        allowedVariables={allowedVariables}
                                        // Note: we don't allow nesting functions inside function slots for simplicity
                                        // allowedFunctions={allowedFunctions}
                                        // allowedOperators={allowedOperators}
                                        // allowGroups={allowGroups}
                                    >
                                        <EmptyToken className="hover:bg-gray-300/50 cursor-pointer" />
                                    </AddTokenDropdownMenu>
                                ) : (
                                    <EmptyToken />
                                )}
                                {index < slots.length - 1 ? ', ' : ''}
                            </React.Fragment>
                        );
                    })}
                </span>
            )}
            )
        </BaseToken>
    );
};

const ConstantToken = ({ className, children, ...props }: React.ComponentPropsWithRef<typeof BaseToken>) => {
    return (
        <BaseToken
            className={classnames('text-green-600 bg-green-100', props.onClick && 'hover:bg-green-200', className)}
            {...props}
        >
            {children}
        </BaseToken>
    );
};

const GroupToken = ({
    className,
    onClick,
    tokens = [],
    onTokensChange,

    allowedVariables = [],
    allowedFunctions = [],
    allowedOperators = [],
    allowGroups = false,
    ...props
}: React.ComponentPropsWithRef<typeof BaseToken> &
    AllowedTokens & { tokens?: (Token | null)[]; onTokensChange?: (tokens: (Token | null)[]) => void }) => {
    const handleClick = (e) => {
        if (!onTokensChange || (e.currentTarget === e.target && onClick)) {
            onClick(e);
        } else {
            e.stopPropagation();
        }
    };

    return (
        <BaseToken
            className={classnames(
                'text-gray-500 bg-gray-100/50 whitespace-normal',
                onClick && 'hover:bg-gray-200/50 ',
                className
            )}
            onClick={handleClick}
            {...props}
        >
            (
            <span className="inline-flex gap-1 px-1">
                <TokenList
                    tokens={tokens}
                    onTokensChange={onTokensChange}
                    allowedVariables={allowedVariables}
                    allowedFunctions={allowedFunctions}
                    allowedOperators={allowedOperators}
                    // allowGroups={allowGroups}
                />
            </span>
            )
        </BaseToken>
    );
};

const TOKEN_TYPE_COMPONENTS = {
    variable: VariableToken,
    constant: ConstantToken,
    operator: OperatorToken,
    function: FunctionToken,
    group: GroupToken,
};

export const TokenList = ({
    className,
    tokens,
    onTokensChange,
    allowedVariables = [],
    allowedFunctions = [],
    allowedOperators = [],
    allowGroups = false,
    ...props
}: React.ComponentPropsWithRef<'div'> &
    AllowedTokens & {
        tokens: Token[];
        onTokensChange?: (tokens: Token[]) => void;
    }) => {
    const handleAddToken = (token: Token, index: number) => {
        const newTokens = [...tokens];
        newTokens.splice(index + 1, 0, token);
        onTokensChange(newTokens);
    };

    const handleUpdateToken = (token: Token, index: number) => {
        const newTokens = [...tokens];
        newTokens[index] = token;
        onTokensChange(newTokens);
    };

    const handleRemoveToken = (index: number) => {
        const newTokens = [...tokens];
        newTokens.splice(index, 1);
        onTokensChange(newTokens);
    };

    return (
        <div className={classnames('flex justify-start gap-2 group', className)} {...props}>
            {tokens.length > 0 && (
                <AddTokenDropdownMenu
                    onTokenSelect={(token) => handleAddToken(token, -1)}
                    allowedVariables={allowedVariables}
                    allowedFunctions={allowedFunctions}
                    allowedOperators={allowedOperators}
                    allowGroups={allowGroups}
                >
                    <EmptyToken className="hover:bg-gray-300/50 cursor-pointer w-0 px-0 transition-all -mr-2 opacity-0 group-hover:w-6 group-hover:mr-0 group-hover:opacity-100 pointer-coarse:w-6 pointer-coarse:mr-0 pointer-coarse:opacity-100 group-open-within:w-6 group-open-within:mr-0 group-open-within:opacity-100" />
                </AddTokenDropdownMenu>
            )}

            {tokens.map(({ type: tokenType, value, ...tokenProps }, index) => {
                const prevToken = index > 0 ? tokens[index - 1] : null;
                const nextToken = index < tokens.length - 1 ? tokens[index + 1] : null;

                const Component = TOKEN_TYPE_COMPONENTS[tokenType];

                if (tokenType === 'function' || tokenType === 'group') {
                    tokenProps['onTokensChange'] = (newTokens: (Token | null)[]) => {
                        handleUpdateToken({ type: tokenType, value, ...tokenProps, tokens: newTokens }, index);
                    };
                    tokenProps['allowedVariables'] = allowedVariables;
                    tokenProps['allowedFunctions'] = allowedFunctions;
                    tokenProps['allowedOperators'] = allowedOperators;
                    tokenProps['allowGroups'] = allowGroups;
                }

                const isCurrentTokenOperator = tokenType === 'operator';
                const isNextTokenOperator = nextToken?.type === 'operator';
                const isNextTokenValueNot = nextToken?.value === 'not';

                const showAddToken = nextToken
                    ? isCurrentTokenOperator
                        ? isNextTokenOperator && !isNextTokenValueNot
                        : !isNextTokenOperator || isNextTokenValueNot
                    : false;

                return (
                    <React.Fragment key={index}>
                        <Component {...tokenProps} onClick={() => handleRemoveToken(index)}>
                            {value}
                        </Component>

                        {showAddToken && (
                            <AddTokenDropdownMenu
                                onTokenSelect={(token) => handleAddToken(token, index)}
                                allowedVariables={allowedVariables}
                                allowedFunctions={allowedFunctions}
                                allowedOperators={allowedOperators}
                                allowGroups={allowGroups}
                            >
                                <EmptyToken className="hover:bg-gray-300/50 cursor-pointer" />
                            </AddTokenDropdownMenu>
                        )}
                    </React.Fragment>
                );
            })}

            <AddTokenDropdownMenu
                onTokenSelect={(token) => handleAddToken(token, tokens.length - 1)}
                keepOpenAfterSelect={true}
                allowedVariables={allowedVariables}
                allowedFunctions={allowedFunctions}
                allowedOperators={allowedOperators}
                allowGroups={allowGroups}
            >
                <EmptyToken className="hover:bg-gray-300/50 cursor-pointer w-0 px-0 transition-all -mr-2 opacity-0 group-hover:w-6 group-hover:mr-0 group-hover:opacity-100 pointer-coarse:w-6 pointer-coarse:mr-0 pointer-coarse:opacity-100 group-open-within:w-6 group-open-within:mr-0 group-open-within:opacity-100" />
            </AddTokenDropdownMenu>
        </div>
    );
};

const AddTokenDropdownMenu = ({
    children,
    onTokenSelect,
    keepOpenAfterSelect = false,
    allowedVariables = [],
    allowedFunctions = [],
    allowedOperators = [],
    allowGroups = false,
}: AllowedTokens & {
    children: React.ReactNode;
    onTokenSelect: (token: Token) => void;
    keepOpenAfterSelect?: boolean;
}) => {
    const { t } = useTranslation('core');

    const [isOpen, setIsOpen] = useState(false);
    const [customValue, setCustomValue] = useState('');

    const handleTokenSelect = (event: Event, token: Token) => {
        if (keepOpenAfterSelect && token.type !== 'function' && token.type !== 'group') {
            event.preventDefault();
        }
        onTokenSelect(token);
    };

    return (
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content align="start" className="z-[60000] w-[20rem]">
                    {allowedOperators.length > 0 && (
                        <>
                            <DropdownMenu.Label>{t('components.rules.operators')}</DropdownMenu.Label>
                            <div className="flex flex-wrap px-3 pb-3 gap-2">
                                {allowedOperators.map((operator) => (
                                    <DropdownMenu.Item
                                        key={operator}
                                        onSelect={(e) => handleTokenSelect(e, { type: 'operator', value: operator })}
                                        className="p-0"
                                    >
                                        <OperatorToken onClick={() => {}}>{operator}</OperatorToken>
                                    </DropdownMenu.Item>
                                ))}
                            </div>
                        </>
                    )}

                    {allowedVariables.length > 0 && (
                        <>
                            <DropdownMenu.Label>{t('components.rules.variables')}</DropdownMenu.Label>
                            <div className="flex flex-wrap px-3 pb-3 gap-2">
                                {allowedVariables.map((variable) => (
                                    <DropdownMenu.Item
                                        key={variable}
                                        onSelect={(e) => handleTokenSelect(e, { type: 'variable', value: variable })}
                                        className="p-0"
                                    >
                                        <VariableToken onClick={() => {}}>{variable}</VariableToken>
                                    </DropdownMenu.Item>
                                ))}
                            </div>
                        </>
                    )}

                    {allowedFunctions.length > 0 && (
                        <>
                            <DropdownMenu.Label>{t('components.rules.functions')}</DropdownMenu.Label>
                            <div className="flex flex-wrap px-3 pb-3 gap-2">
                                {allowedFunctions.map((func) => (
                                    <DropdownMenu.Item
                                        key={func.name}
                                        onSelect={(e) =>
                                            handleTokenSelect(e, {
                                                type: 'function',
                                                value: func.name,
                                                slotCount: func.slotCount,
                                            })
                                        }
                                        className="p-0"
                                    >
                                        <FunctionToken onClick={() => {}} slotCount={func.slotCount}>
                                            {func.name}
                                        </FunctionToken>
                                    </DropdownMenu.Item>
                                ))}
                            </div>
                        </>
                    )}

                    {allowGroups && (
                        <>
                            <DropdownMenu.Label>{t('components.rules.groups')}</DropdownMenu.Label>
                            <div className="flex flex-wrap px-3 pb-3 gap-2">
                                <DropdownMenu.Item
                                    onSelect={(e) => handleTokenSelect(e, { type: 'group', value: '()', tokens: [] })}
                                    className="p-0"
                                >
                                    <GroupToken onClick={() => {}} tokens={[]} />
                                </DropdownMenu.Item>
                            </div>
                        </>
                    )}

                    <DropdownMenu.Label>{t('components.rules.constants')}</DropdownMenu.Label>
                    <div className="flex flex-col px-3 pb-3 gap-2">
                        {[NUMBER_CONSTANT_SUGGESTION, DAY_CONSTANT_SUGGESTION, BOOLEAN_CONSTANT_SUGGESTION].map(
                            (constantGroup, idx) => (
                                <div className="flex flex-wrap gap-2" key={idx}>
                                    {constantGroup.map((constant) => (
                                        <DropdownMenu.Item
                                            key={constant}
                                            onSelect={(e) =>
                                                handleTokenSelect(e, { type: 'constant', value: constant })
                                            }
                                            className="p-0"
                                        >
                                            <ConstantToken onClick={() => {}}>{constant}</ConstantToken>
                                        </DropdownMenu.Item>
                                    ))}
                                </div>
                            )
                        )}

                        <div className="flex gap-2 w-full">
                            <input
                                className="w-full border border-gray-300 px-2 py-1 rounded text-sm"
                                placeholder={t('components.rules.customValuePlaceholder')}
                                value={customValue}
                                onChange={(e) => setCustomValue(e.target.value)}
                            />

                            <Button
                                className="py-1 px-2"
                                onClick={() => {
                                    const isNumber = !isNaN(Number(customValue));
                                    onTokenSelect({
                                        type: 'constant',
                                        value: isNumber ? customValue : `"${customValue}"`,
                                    });
                                    setCustomValue('');
                                    if (!keepOpenAfterSelect) {
                                        setIsOpen(false);
                                    }
                                }}
                                type="button"
                            >
                                {t('components.rules.customValueAddButton')}
                            </Button>
                        </div>
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu>
    );
};
